import { rem } from 'polished'
import { useContext, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import TextButton from 'LuxKit/Button/TextButton'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { trackClientEvent } from 'analytics/analytics'
import FormatCurrency from 'components/Common/FormatCurrency'
import PayAtPropertyPill from 'components/Common/PayAtPropertyPill'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { mediaQueryOnly } from 'components/utils/breakpoint'
import CurrencyContext from 'contexts/currencyContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'

import PropertyCancellationPolicy from './PropertyCancellationPolicy'
import { showsPropertyCancellationTimeline } from 'lib/offer/cancellationPolicyUtils'

const PaymentOptionsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  padding-top: 1rem;

  ${mediaQueryOnly.mobile} {
    grid-template-columns: 1fr;
  }
`

const DescriptionText = styled(BodyText)`
  min-height: ${rem(60)}; // Approximately 3 lines of text
`

const trackPayAtPropertyModalEvent = (action: string, type: 'interaction' | 'nonInteraction') => {
  trackClientEvent({
    subject: 'choose_payment_option_modal',
    action,
    category: 'pay_at_property',
    type,
  })
}

interface Props {
  propertyTimeZone: string
  duration: number
  propertyCollectRate: App.EnrichedPropertyCollectPropertyRate
  leCollectRate: App.EnrichedLeCollectPropertyRate
  optInBundlesPrice?: number
}

function PropertyPayAtPropertyModal({
  propertyTimeZone,
  duration,
  propertyCollectRate,
  leCollectRate,
  optInBundlesPrice = 0,
}: Props) {
  const intl = useIntl()
  const { resolve } = useModalElementContext<App.EnrichedPropertyRate | undefined>()
  const contextCurrency = useContext(CurrencyContext)

  useEffect(() => {
    trackPayAtPropertyModalEvent('impression', 'nonInteraction')
  }, [])

  const onSelectPayAtProperty = () => {
    trackPayAtPropertyModalEvent('select_pay_at_property', 'interaction')
    resolve(propertyCollectRate)
  }

  const onSelectPayNow = () => {
    trackPayAtPropertyModalEvent('select_pay_now', 'interaction')
    resolve(leCollectRate)
  }

  const onClose = () => {
    resolve(undefined)
  }

  const propertyCollectRateHeadlineCancellationPolicyStart = propertyCollectRate.headlineCancellationPolicyStart ? new Date(propertyCollectRate.headlineCancellationPolicyStart) : undefined
  const leCollectRateHeadlineCancellationPolicyStart = leCollectRate.headlineCancellationPolicyStart ? new Date(leCollectRate.headlineCancellationPolicyStart) : undefined

  return <ModalBase size="L">
    <ModalHeader
      title={intl.formatMessage({
        id: '-gzeFtm8OnyonFNBYR09',
        defaultMessage: 'Your payment options',
        description: 'Title for pay at property modal showing payment choices',
      })}
      onCloseButtonClick={onClose}
    />
    <ModalBody>
      <ModalContent>
        <PaymentOptionsContainer>
          {/* Pay at Property Option */}
          <Sheet
            border="light-foreground"
            borderRadius="S"
            padding={20}
          >
            <VerticalSpacer gap={12}>
              <Group
                direction="horizontal"
                gap={8}
                horizontalAlign="space-between"
              >
                <Heading variant="heading4">
                  <FormattedMessage
                    id="p4Cx_trTzGJ68sJaTa8y"
                    defaultMessage="Pay {amount} now"
                    description="Section heading for pay at property option on the offer detail page"
                    values={{ amount: <FormatCurrency value={0} format="roundedDollar" /> }}
                  />
                </Heading>
                <PayAtPropertyPill hasMultipleOptions={false} />
              </Group>

              {!!propertyCollectRate.paymentMethod.totals && <DescriptionText variant="medium" colour="light-foreground-alt">
                <FormattedMessage
                  id="8Xja8hHlP5h7Pk_no4vC"
                  defaultMessage="Prices in {currency} are estimated, you will pay in the property’s preferred currency ({amount})"
                  description="Explanation that pay-at-property prices are estimated in user currency and actual payment will be in property currency"
                  values={{
                    currency: contextCurrency,
                    amount: <FormatCurrency value={propertyCollectRate.paymentMethod.totals.displayPrice} currency={propertyCollectRate.paymentMethod.totals.currency} />,
                  }}
                />
              </DescriptionText>}

              <PropertyCancellationPolicy
                propertyTimeZone={propertyTimeZone}
                headlineCancellationPolicyType={propertyCollectRate.headlineCancellationPolicyType}
                showCancellationTimeline={showsPropertyCancellationTimeline(propertyCollectRate)}
                headlineCancellationPolicyStart={propertyCollectRateHeadlineCancellationPolicyStart}
                fullyRefundableUntil={propertyCollectRate.fullyRefundableUntil}
                cancellationPolicies={propertyCollectRate.cancellationPolicies}
                currency={propertyCollectRate.currency}
                shouldClarifyRefundIsHotelOnly={
                  // This _should_ be `false`, as we cannot offer property collect for packaged
                  // rates (i.e. hotel with flights).
                  propertyCollectRate.includesFlights
                }
              />
              <VerticalSpacer gap={leCollectRate.luxPlusTotals?.displayPrice ? 30 : 12}>
                <Group direction="vertical" gap={2}>
                  <PriceRowPriceCaption>
                    {intl.formatMessage(
                      {
                        id: 'WJfp1mfVoBPd5B3l4RVD',
                        defaultMessage: '{count, plural, one {# night} other {# nights}}',
                        description: 'Night count for pay-at-property rate on the offer detail page',
                      },
                      { count: duration },
                    )}
                  </PriceRowPriceCaption>

                  <PriceRowPrice price={propertyCollectRate.totals.displayPrice + optInBundlesPrice} saleUnit="total" size="L" />

                  <PriceRowTaxesAndFees />
                </Group>

                <TextButton
                  size="large"
                  kind="primary"
                  fit="full-width"
                  onClick={onSelectPayAtProperty}
                >
                  <FormattedMessage
                    id="4-AkdhSguWIycROZnzFF"
                    defaultMessage="Pay at property"
                    description="Button to select pay-at-property payment option on the form"
                  />
                </TextButton>
              </VerticalSpacer>
            </VerticalSpacer>
          </Sheet>

          {/* Pay Now Option */}
          <Sheet
            border="light-foreground"
            borderRadius="S"
            padding={20}
          >
            <VerticalSpacer gap={12}>
              <Heading variant="heading4">
                <FormattedMessage
                  id="vbhB-HGmOpGRx1zXfkZe"
                  defaultMessage="Pay in full now"
                  description="Section heading for pay in full option on the offer detail page"
                />
              </Heading>

              {!!propertyCollectRate.paymentMethod.totals && <DescriptionText variant="medium" colour="light-foreground-alt">
                <FormattedMessage
                  id="0O2fsvxkOVLdmfQgeKBC"
                  defaultMessage="We process your payment now in your local currency"
                  description="Explanation that pay-now option processes payment in user’s local currency"
                />
              </DescriptionText>}

              <PropertyCancellationPolicy
                propertyTimeZone={propertyTimeZone}
                headlineCancellationPolicyType={leCollectRate.headlineCancellationPolicyType}
                showCancellationTimeline={showsPropertyCancellationTimeline(leCollectRate)}
                headlineCancellationPolicyStart={leCollectRateHeadlineCancellationPolicyStart}
                fullyRefundableUntil={leCollectRate.fullyRefundableUntil}
                cancellationPolicies={leCollectRate.cancellationPolicies}
                currency={leCollectRate.currency}
                shouldClarifyRefundIsHotelOnly={
                  // This _should_ be `false`, as we cannot offer property collect for packaged
                  // rates (i.e. hotel with flights).
                  leCollectRate.includesFlights
                }
              />
              <Group direction="vertical" gap={2}>
                <PriceRowPriceCaption>
                  {intl.formatMessage(
                    {
                      id: 'jUE4uud3J8jYI8615-hZ',
                      defaultMessage: '{count, plural, one {# night} other {# nights}}',
                      description: 'Pluralised count of nights for the pay-now rate option in the hotel booking pricing',
                    },
                    { count: duration },
                  )}
                </PriceRowPriceCaption>
                <LuxPlusPriceGuard
                  showMemberPrice={!!leCollectRate.luxPlusTotals?.displayPrice}
                  fallbackPrice={<PriceRowPrice
                    size="M"
                    signDisplay="negative"
                    price={leCollectRate.totals.displayPrice + optInBundlesPrice}
                    saleUnit="total"
                  />}
                >
                  <LuxPlusPriceStack
                    size="M"
                    signDisplay="negative"
                    price={leCollectRate.totals.displayPrice + optInBundlesPrice}
                    memberPrice={leCollectRate.luxPlusTotals?.displayPrice ? leCollectRate.luxPlusTotals.displayPrice + optInBundlesPrice : 0}
                    saleUnit="total"
                  />
                </LuxPlusPriceGuard>

                <PriceRowTaxesAndFees />
              </Group>

              <TextButton
                size="large"
                kind="primary"
                fit="full-width"
                onClick={onSelectPayNow}
              >
                <FormattedMessage
                  id="tJnR_dIaXR4yONsUZeJF"
                  defaultMessage="Pay now"
                  description="Button to select pay-now payment option during the checkout and payment process"
                />
              </TextButton>
            </VerticalSpacer>
          </Sheet>
        </PaymentOptionsContainer>
      </ModalContent>
    </ModalBody>
  </ModalBase>
}

export default PropertyPayAtPropertyModal
